import { memo } from 'preact/compat';
import { useAppDispatch, useAppSelector } from '../hooks';
import { setSelectedCode, hideOffer, restoreOffer } from '../store/offersSlice';
import { selectFilteredOffers } from '../store/selectors';
import { useState } from 'preact/hooks';
import AddSuggestionModal from './AddSuggestionModal';
import AdminPage from './AdminPage';
import FilterBar from './FilterBar';
import LoadingOverlay from './LoadingOverlay';
import { api } from '../api/client';
import type { ISidebarProps, IOffer } from '../types';

const Sidebar = memo(({ onToggleSidebar, isCollapsed = false }: ISidebarProps) => {
    const dispatch = useAppDispatch();
    const filteredItems = useAppSelector(selectFilteredOffers);
    const selectedCode = useAppSelector(s => s.offers.selectedCode);
    const loading = useAppSelector(s => s.offers.loading);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showAdmin, setShowAdmin] = useState(false);
    const isAdmin = document.cookie.includes('b24_is_admin=1');
    const [showHidden, setShowHidden] = useState(false);
    const [hiddenOffers, setHiddenOffers] = useState<IOffer[]>([]);
    const [hiddenLoaded, setHiddenLoaded] = useState(false);
    const [hiddenLoading, setHiddenLoading] = useState(false);
    const [animatingCode, setAnimatingCode] = useState<number | null>(null);

    const loadHidden = async () => {
        setHiddenLoading(true);
        try {
            setHiddenOffers(await api.getHiddenOffers());
        } catch { setHiddenOffers([]); }
        setHiddenLoaded(true);
        setHiddenLoading(false);
    };

    const handleShowHidden = () => {
        setShowHidden(true);
        if (!hiddenLoaded) loadHidden();
    };

    const handleHide = async (e: Event, code: number) => {
        e.stopPropagation();
        setAnimatingCode(code);
        await dispatch(hideOffer(code));
        setAnimatingCode(null);
        setHiddenLoaded(false);
    };

    const handleRestore = async (code: number) => {
        setAnimatingCode(code);
        await dispatch(restoreOffer(code));
        setHiddenOffers(prev => prev.filter(o => Number(o.code) !== code));
        setAnimatingCode(null);
        setHiddenLoaded(false);
    };

    if (isCollapsed) {
        return (
            <div
                className="w-12 bg-base-100 h-screen border-r border-base-300 cursor-pointer hover:bg-base-200 transition-colors flex items-center justify-center"
                onClick={onToggleSidebar}
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-base-content/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
            </div>
        );
    }

    const displayItems = showHidden ? hiddenOffers : filteredItems;

    return (
        <div className="w-80 bg-base-100 h-screen flex flex-col border-r border-base-300 transition-all relative">
            <LoadingOverlay visible={loading} />
            <div className="p-4 border-b border-base-300">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold">Предложения</h2>
                    <div className="flex items-center gap-1">
                        <FilterBar />
                        <button className="p-0 cursor-pointer text-base-content/50 hover:text-base-content transition-colors" onClick={onToggleSidebar} title="Свернуть">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="flex gap-2 mt-2">
                    <button className={`btn btn-xs flex-1 ${!showHidden ? 'btn-accent' : 'btn-ghost'}`} onClick={() => setShowHidden(false)}>
                        Активные
                    </button>
                    <button className={`btn btn-xs flex-1 ${showHidden ? 'btn-accent' : 'btn-ghost'}`} onClick={handleShowHidden}>
                        Скрытые {hiddenOffers.length > 0 || showHidden ? '' : ''}
                    </button>
                </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 relative">
                {(showHidden && hiddenLoading) && <LoadingOverlay visible />}
                {displayItems.length === 0 && !hiddenLoading ? (
                    <div className="text-center py-8 text-base-content/60">
                        {showHidden ? 'Нет скрытых предложений' : 'Нет предложений'}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {displayItems.map((offer) => {
                            const isAnimating = animatingCode === Number(offer.code);

                            if (isAnimating) {
                                return (
                                    <div key={offer.code} className="p-3 border-b border-base-300 animate-pulse">
                                        <div className="h-5 bg-base-300 rounded w-2/3 mb-3" />
                                        <div className="space-y-2">
                                            <div className="h-3 bg-base-300 rounded w-full" />
                                            <div className="h-3 bg-base-300 rounded w-4/5" />
                                            <div className="h-3 bg-base-300 rounded w-3/5" />
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div
                                    key={offer.code}
                                    className={`p-3 border-b border-base-300 cursor-pointer hover:bg-base-200 transition ${Number(offer.code) === selectedCode ? 'bg-accent/10 border-accent' : ''}`}
                                    onClick={() => dispatch(setSelectedCode(Number(offer.code)))}
                                >
                                    <div className="flex justify-between items-start">
                                        <h3 className="font-semibold text-base truncate flex-1">
                                            {offer.offer_id || `#${offer.code}`}
                                        </h3>
                                        {showHidden ? (
                                            <button className="btn btn-xs btn-ghost text-success ml-2" onClick={() => handleRestore(Number(offer.code))} title="Восстановить">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 010 10H9m4-10l-3-3m3 3l-3 3" />
                                                </svg>
                                            </button>
                                        ) : (
                                            <button className="btn btn-xs btn-ghost text-base-content/40 hover:text-error ml-2" onClick={e => handleHide(e, Number(offer.code))} title="Скрыть">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    <div className="space-y-1 text-sm text-base-content mt-1">
                                        <div className="flex justify-between">
                                            <span className="text-base-content/60">Статус:</span>
                                            <span>{offer.status_name || '—'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-base-content/60">Поставщик:</span>
                                            <span className="truncate ml-2">{offer.vendor_name || '—'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-base-content/60">Дата:</span>
                                            <span>{offer.dat ? new Date(offer.dat).toLocaleDateString('ru-RU') : '—'}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className="shrink-0 p-4 border-t border-base-300">
                <div className="flex gap-2">
                    <button className="btn btn-accent btn-sm flex-1" onClick={() => setIsModalOpen(true)}>
                        Создать предложение
                    </button>
                    {isAdmin && (
                        <button className="btn btn-sm btn-ghost p-1" onClick={() => setShowAdmin(true)} title="Администрирование">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-base-content/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
            <AddSuggestionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

            {showAdmin && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setShowAdmin(false)}>
                    <div className="bg-base-100 rounded-lg shadow-2xl w-[90vw] max-w-5xl h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center px-4 py-3 border-b border-base-300">
                            <h2 className="font-semibold">Администрирование</h2>
                            <button className="btn btn-ghost btn-sm" onClick={() => setShowAdmin(false)}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <AdminPage />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default Sidebar;
